import { PaymentTypeEnum } from 'types'

export interface PaginationState {
  page: number
  pageSize: number
  total: number
}

export type VcnProviderFilter = 'WEX' | 'MARQETA'

export type PrimaryPaymentMethodFilter =
  | PaymentTypeEnum.ACH
  | PaymentTypeEnum.VCN
  | PaymentTypeEnum.CHECK
  | PaymentTypeEnum.GROUP_ACH

/** Migration-dashboard metric a summary card scopes the grid to (matches the EPS enum). */
export type VcnDashboardMetric =
  | 'marqeta_benefits'
  | 'wex_benefits'
  | 'marqeta_active'
  | 'wex_active'
  | 'overlap'
  | 'cleanup'

/** VCN card lifecycle status (row data + filters). */
export enum VcnCardStatus {
  ACTIVE = 'ACTIVE',
  SUSPENDED = 'SUSPENDED',
  TERMINATED = 'TERMINATED',
}

/** Minimal row for VCN bulk termination (map from API when available). */
export interface VcnManagementBenefitRow {
  benefitId: string
  companyName: string
  /** When set, matches selected company keys from the admin company list. */
  clientPublicKey?: string
  /**
   * Employee the benefit belongs to. Drives the expandable "Associated VCNs"
   * row (keys the GET employees/:epk/cards call). Populated once EPS exposes it
   * on the list response; rows without it are not expandable.
   */
  employeePublicKey?: string
  carrierName: string
  /** Payment provider (WEX/MARQETA for VCN, iStream for ACH). */
  provider: string
  /** Set when mapping list API response (VCN rows only). */
  vcnStatus?: VcnCardStatus
  vcnToken: string
  lastFourDigits: string
  primaryPaymentMethod?: PrimaryPaymentMethodFilter
  /** Insured person on the benefit (first + last name). */
  insuredPersonName?: string
}

/** Filters: top bar (search) + drawer (company, carrier, payment, provider, status). */
export interface VcnManagementBenefitFilters {
  search?: string
  clientPublicKeys?: string[]
  carrierName?: string
  vcnProvider?: VcnProviderFilter
  vcnStatus?: VcnCardStatus
  primaryPaymentMethod?: PrimaryPaymentMethodFilter
  /**
   * Migration-dashboard scope from a summary-card click. Mutually exclusive with
   * the normal filters above — set alone (others cleared) while a card is active.
   */
  dashboardMetric?: VcnDashboardMetric
}

/**
 * Whether a benefit is currently using this card. Computed by EPS and shared
 * with the card tiles, so the tile and the grid it filters cannot disagree —
 * the client never re-derives it.
 */
export enum VcnCardInUseStatus {
  IN_USE = 'IN_USE',
  IN_USE_ENDED = 'IN_USE_ENDED',
  NOT_IN_USE = 'NOT_IN_USE',
}

/** One row of Card Mode — one VCN card. Mirrors EPS `VcnManagementCardRowDto`. */
export interface VcnManagementCardRow {
  /** WEX merchant_log_id / MARQETA card_public_key — the key card actions use. */
  virtualCardKey: string
  provider: VcnProviderFilter
  /** The CARD's own status, not the benefit's. */
  cardStatus: VcnCardStatus
  inUse: VcnCardInUseStatus
  /** The benefit using this card; absent for an orphan. */
  benefitId?: string
  companyName?: string
  clientPublicKey?: string
  employeePublicKey?: string
  insuredPersonName?: string
  /** Absent for an orphan — a card carries no plan reference. */
  carrierName?: string
  primaryPaymentMethod?: PrimaryPaymentMethodFilter
  vcnToken?: string
  lastFourDigits?: string
}

/**
 * Columns the card list can be ordered by, mirroring what EPS accepts.
 *
 * The four card-grain ones are cheap — EPS orders the cards and enriches only
 * the page. The six merged-field ones only exist after its cross-database join,
 * so sorting by one costs the same as filtering on one.
 */
export enum VcnCardListSortBy {
  PROVIDER = 'provider',
  CARD_STATUS = 'cardStatus',
  LAST_FOUR_DIGITS = 'lastFourDigits',
  VCN_TOKEN = 'vcnToken',
  INSURED_PERSON_NAME = 'insuredPersonName',
  BENEFIT_ID = 'benefitId',
  COMPANY_NAME = 'companyName',
  CARRIER_NAME = 'carrierName',
  PRIMARY_PAYMENT_METHOD = 'primaryPaymentMethod',
  IN_USE = 'inUse',
}

/** Uppercase to match every other sorted endpoint — see @surecompanies/contracts. */
export enum VcnSortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** What the grid is currently ordered by, or null for the default newest-first. */
export interface VcnCardListSort {
  sort: VcnCardListSortBy
  order: VcnSortOrder
}

/**
 * Card Mode's sidebar set. Narrower than Benefit Mode's on purpose:
 * `primaryPaymentMethod` filters nothing when every row is a card, and
 * `includeInactiveBenefits` is subsumed by `inUse`.
 */
export interface VcnManagementCardFilters {
  search?: string
  vcnProvider?: VcnProviderFilter
  /** The card's own status — named cardStatus, not vcnStatus, to match EPS. */
  cardStatus?: VcnCardStatus
  /** Several values: the Orphan tile is IN_USE_ENDED + NOT_IN_USE together. */
  inUse?: VcnCardInUseStatus[]
  /** Employer of the card HOLDER, so orphan cards stay findable by client. */
  clientPublicKeys?: string[]
  /** Carrier of the bound benefit — necessarily excludes orphan cards. */
  carrierName?: string
}
